import { useCallback, useEffect, useState } from 'react'

import { Heading, SocialLinkPreview } from '@hhgtech/hhg-components'
import { Carousel, Text } from '@hhgtech/hhg-components/mantine'
import { CarouselProps } from '@mantine/carousel'
import {
  Box,
  BoxProps,
  Flex,
  ScrollArea,
  Stack,
  rem,
  useMantineTheme,
} from '@mantine/core'
import { useIntersection } from '@mantine/hooks'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { Container } from 'components/atoms/container'
import { Loading } from 'components/atoms/loading'
import { useIsMobile } from 'hooks/useIsMobile'
import { SpotlightVideoSession } from 'interfaces/types'

import useStyles from './index.styles'
import { VideoItem } from './videoItem'
export type VideoLibraryProps = {
  sessionProps: SpotlightVideoSession
  idScroll: string
  autoPlay?: boolean
  applyH1?: boolean
} & BoxProps

export type HandlerProps = {
  setPause: (state: boolean) => void
  getIdScroll: () => string
}

const VideoLibrary = ({
  sessionProps,
  styles,
  autoPlay: autoPlayProp = false,
  applyH1,
  ...boxProps
}: VideoLibraryProps) => {
  const { backgroundColor } = sessionProps
  const { classes } = useStyles(
    { bgColor: backgroundColor },
    { name: 'VideoLibrary', styles },
  )
  const { formatMessage: f } = useIntl()
  const { title, videoList = [] } = sessionProps
  const [videosInfo, setVideosInfo] = useState<VideoItem[]>([])
  const [loading, setLoading] = useState(false)
  const [heroVideo, setHeroVideo] = useState<VideoItem | null>(
    videoList?.[0]
      ? { title: videoList[0].videoTitle, url: videoList[0].urlVideo }
      : null,
  )
  const [autoPlay, setAutoPlay] = useState(autoPlayProp)
  const [number, setNumber] = useState(1)
  const isMobile = useIsMobile()
  const mantineTheme = useMantineTheme()
  const hasOtherList = videosInfo.length > 1
  // const heroVidRef = useRef<HTMLDivElement>(null)
  const { ref: heroVidRef, entry } = useIntersection({
    threshold: 1,
  })

  // useImperativeHandle(ref, () => ({
  //   setPause: (state: boolean) => {
  //     if (!autoPlay) return
  //     heroVidRef?.current
  //       ?.querySelector('iframe')
  //       ?.contentWindow?.postMessage(
  //         `{"event":"command","func":"${
  //           state ? 'pauseVideo' : 'playVideo'
  //         }","args":""}`,
  //         '*',
  //       )
  //   },
  //   getIdScroll: () => idScroll,
  // }))

  const fetchPreview = useCallback(async () => {
    try {
      setLoading(true)
      const res = await Promise.all(
        videoList.map((i) =>
          callApiNew(
            PATHS.TOGETHER.FETCH_PREVIEW,
            {
              method: 'POST',
              data: {
                link: i.urlVideo,
              },
            },
            'together',
          ),
        ),
      )
      setLoading(false)
      const dataRes: VideoItem[] = res
        .map((i, idx) => {
          return (
            i?.data && {
              ...i.data,
              url: videoList[idx].urlVideo,
              title: videoList[idx].videoTitle || i.title,
            }
          )
        })
        .filter(Boolean)
      if (dataRes.length > 0) {
        setHeroVideo(dataRes?.[0])
        setVideosInfo(dataRes)
      }
    } catch (err) {
      console.log(err)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    videoList.length > 0 && fetchPreview()
  }, [])

  useEffect(() => {
    if (entry && autoPlay) {
      entry?.target
        ?.querySelector('iframe')
        ?.contentWindow?.postMessage(
          `{"event":"command","func":"${
            entry.isIntersecting ? 'playVideo' : 'pauseVideo'
          }","args":""}`,
          '*',
        )
    }
  }, [entry])

  const carouselProps: CarouselProps = {
    slidesToScroll: 1,
    slideSize: '45%',
    slideGap: rem(8),
    withIndicators: true,
    withControls: false,
    align: 'start',
    loop: true,
    styles: () => ({
      indicators: {
        bottom: 'auto',
        top: 'calc(100% + 30px - 8px)',
      },
      indicator: {
        width: '8px',
        height: '8px',
        // border: `1px solid #fff`,
        opacity: 1,
        backgroundColor: mantineTheme.colors.neutral[5],
        '&[data-active]': {
          backgroundColor: mantineTheme.colors.blue[7],
        },
        '.mantine-Carousel-control[data-inactive]': {
          opacity: 0,
          cursor: 'default',
        },
      },
    }),
  }

  const countEle =
    !loading && videoList.length > 1 ? (
      <Text size="s4">
        {f(
          {
            id: 'common.currentOnTotalVideo',
            defaultMessage: 'Total video {number}/{count}',
          },
          {
            number,
            count: videoList.length,
          },
        )}
      </Text>
    ) : null

  return videoList.length > 0 ? (
    <Box className={classes.root} {...boxProps}>
      <Container>
        <Stack
          spacing={rem(40)}
          sx={(theme) => ({
            gap: rem(24),
            [theme.fn.largerThan('sm')]: { gap: rem(40) },
          })}
        >
          <Stack spacing={rem(8)}>
            {title && (
              <Text
                size="h2"
                as={applyH1 ? 'h1' : 'h2'}
                className={classes.heading}
              >
                {title}
              </Text>
            )}
            {isMobile && countEle}
          </Stack>
          {loading ? (
            <Loading />
          ) : videosInfo.length > 0 ? (
            <Flex
              ref={heroVidRef}
              gap={rem(24)}
              sx={(theme) => ({
                [theme.fn.smallerThan('sm')]: {
                  flexDirection: 'column',
                  gap: rem(8),
                },
              })}
            >
              {heroVideo && (
                <Stack
                  spacing={rem(16)}
                  sx={(theme) => ({
                    backgroundColor: hasOtherList ? '#000' : 'transparent',
                    [theme.fn.largerThan('sm')]: {
                      width: hasOtherList ? '65%' : '100%',
                    },
                  })}
                >
                  <SocialLinkPreview
                    key={heroVideo.url}
                    url={heroVideo.url || ''}
                    autoPlay={autoPlay}
                  />
                  {!hasOtherList && (
                    <Heading tag="h3">{heroVideo.title}</Heading>
                  )}
                </Stack>
              )}
              {hasOtherList &&
                (!isMobile ? (
                  <Box
                    sx={{
                      position: 'relative',
                      width: '35%',
                    }}
                  >
                    {countEle}
                    <Box
                      sx={(theme) => ({
                        [theme.fn.largerThan('sm')]: {
                          position: 'absolute',
                          top: rem(30),
                          left: 0,
                          right: 0,
                          bottom: 0,
                        },
                      })}
                    >
                      <ScrollArea
                        h="100%"
                        type="always"
                        offsetScrollbars
                        scrollbarSize={8}
                      >
                        <Flex
                          sx={(theme) => ({
                            gap: rem(8),
                            [theme.fn.largerThan('sm')]: {
                              flexDirection: 'column',
                              gap: rem(24),
                            },
                          })}
                        >
                          {videosInfo.map((i, idx) => (
                            <VideoItem
                              key={String(idx)}
                              indexNumber={idx + 1}
                              showIndex={!isMobile}
                              title={i.title}
                              url={i.url}
                              image={i.image}
                              active={i.url === heroVideo?.url}
                              onClick={() => {
                                setNumber(idx + 1)
                                setAutoPlay(true)
                                setHeroVideo(i)
                              }}
                              sx={{
                                cursor: 'pointer',
                              }}
                            />
                          ))}
                        </Flex>
                      </ScrollArea>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ paddingBottom: rem(30) }}>
                    <Carousel {...carouselProps}>
                      {videosInfo.map((i, idx) => (
                        <Carousel.Slide key={String(idx)}>
                          <VideoItem
                            title={i.title}
                            url={i.url}
                            image={i.image}
                            active={i.url === heroVideo?.url}
                            onClick={() => {
                              setAutoPlay(true)
                              setHeroVideo(i)
                            }}
                            sx={{
                              cursor: 'pointer',
                            }}
                          />
                        </Carousel.Slide>
                      ))}
                    </Carousel>
                  </Box>
                ))}
            </Flex>
          ) : null}
        </Stack>
      </Container>
    </Box>
  ) : null
}

export { VideoLibrary }
